import React, { useEffect, useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ListingStats.module.css';

/**
 * ListingStats — shows listing view count and other stats.
 *
 * Reads stats from:
 *   1. listing.attributes.publicData.viewCount (set by listingsApi feature)
 *   2. listing.attributes.metadata.viewCount (if set via Integration API)
 *
 * Also tracks the current view by calling POST /api/listings/update-view-count
 * if the listingsApi feature is enabled.
 *
 * @param {Object} props.listing — Sharetribe listing entity
 * @param {string} [props.className]
 * @param {boolean} [props.trackView] — auto-increment view count (default: true)
 */
const ListingStats = ({ listing, className, trackView }) => {
  const [viewCount, setViewCount] = useState(
    listing?.attributes?.publicData?.viewCount ||
    listing?.attributes?.metadata?.viewCount ||
    0
  );

  const listingId = listing?.id?.uuid;

  useEffect(() => {
    if (!listingId || !trackView) return;

    // Track view
    fetch('/api/listings/update-view-count', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listingId }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.viewCount) setViewCount(data.viewCount);
      })
      .catch(() => {}); // Silent fail — stats are not critical
  }, [listingId, trackView]); // eslint-disable-line

  return (
    <div className={classNames(css.root, className)}>
      <span className={css.stat}>
        <svg className={css.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <FormattedMessage id="ListingStats.views" values={{ count: viewCount }} />
      </span>
    </div>
  );
};

ListingStats.defaultProps = {
  className: null,
  trackView: true,
};

ListingStats.propTypes = {
  className: string,
};

export default ListingStats;
